<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="projects-page-title" :class="{'mb-1': getGuide('asset_projects')}">
      <button class="btn btn-secondary-dark float-right"
              @click="handleFilterButton(false)"
              qid="projects-filter-button"
      >
        <font-awesome-icon icon="filter"/>
      </button>
      <CreateProjectModal
          class="float-right ml-3"
          type="update"
          :types="types"
      ></CreateProjectModal>
      <b-form-input
        type="text"
        v-model="filters.keywordValue"
        class="float-right mw-300 search-bar"
        qid="search-s-input"
        v-on:input="debounceInput"
        :placeholder="$t('projects.search_placeholder')"
      ></b-form-input>
      {{ $t('projects.asset_project_list_title') }}
    </h4>
    <div>
      <span v-if="getGuide('asset_projects')">
        <GuideButton :guide="getGuide('asset_projects')"></GuideButton>
      </span>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">

          <table class="table table-responsive-md table-list">
            <thead>
              <tr>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('title')"
                    qid="sort-s-name"
                >
                  {{ $t('projects.table.name') }}
                  <span class="ml-2">
                    <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'title'"/>
                    <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'title'"/>
                    <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'title'"/>
                  </span>
                </th>
                <th scope="col" v-if="tableColumns.responsibles">
                  {{ $t('projects.table.owners') }}
                </th>
                <th scope="col" v-if="tableColumns.tags">
                  {{ $t('processes.table.tags') }}
                </th>
                <th scope="col" v-if="tableColumns.type">
                  {{ $t('projects.table.type') }}
                </th>
                <th scope="col" v-if="tableColumns.scope">
                  {{ $t('projects.table.scope') }}
                </th>
                <th scope="col" v-if="tableColumns.schedule">
                  {{ $t('projects.create.schedule_label') }}
                </th>
                <th scope="col" v-if="tableColumns.status">
                  {{ $t('system.status') }}
                </th>
                <th width="180" class="text-right">
                  <b-nav-item-dropdown right extra-toggle-classes="no-dropdown-caret" class="guide-settings">
                    <!-- Using 'button-content' slot -->
                    <template slot="button-content">
                      <span class="float-right" style="list-style: none;">
                        <font-awesome-icon size="lg" icon="cog" class="text-muted"/>
                      </span>
                    </template>

                    <div class="pl-4 pr-4">
                      <b-form-checkbox
                        v-model="tableColumns.responsibles"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('systems.table.owners') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.tags"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('systems.table.tags') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.type"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('projects.table.type') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.scope"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('projects.table.scope') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.schedule"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('projects.create.schedule_label') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.status"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('system.status') }}
                      </b-form-checkbox>
                      <br>
                    </div>
                    <hr>
                    <div class="pl-4 pr-4">
                      <small class="text-muted">
                        * {{ $t('system.global_table_expand_explainer_text') }}
                      </small>
                    </div>
                  </b-nav-item-dropdown>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <router-link
                      :to="{ name: 'projects.view', params: { slug: object.slug }}"
                      qid="s-view-page-link"
                  >
                    {{ object.title }}
                  </router-link>
                </td>
                <td v-if="tableColumns.responsibles">
                  <span class="mr-1 text-normal" v-for="user in getReducedUsers(object.users)">
                    <span qid="profile-photo">
                      <Avatar :object="user" size="xs"></Avatar>
                    </span>
                  </span>
                  <span v-if="object.users.length > 3"
                        class="badge cursor-pointer"
                        @click="openUsersModal(object)"
                  >
                    <font-awesome-icon icon="users"/>
                    + {{ object.users.length - 3 }}
                  </span>
                </td>
                <td v-if="tableColumns.tags">
                  <span v-for="tag in getReducedTags(object.tags_translated)" qid="p-tags-list">
                    <span class="badge" :title="tag.name_translated">
                      <font-awesome-icon icon="tag"/>
                      {{ tag.name_translated | truncate(35) }}
                    </span>
                  </span>
                  <span v-if="object.tags_translated.length > 3"
                        class="badge cursor-pointer"
                        @click="openTagsModal(object)"
                  >
                    <font-awesome-icon icon="tags"/>
                    + {{ object.tags_translated.length - 3 }}
                  </span>
                </td>
                <td v-if="tableColumns.type">
                  <span v-if="object.type == 'update'" class="badge">
                    {{ $t('projects.view.update_label') }}
                  </span>
                  <span v-if="object.type == 'validation'" class="badge">
                    {{ $t('projects.view.validation_label') }}
                  </span>
                  <span v-if="object.type == 'control'" class="badge">
                    {{ $t('projects.view.control_label') }}
                  </span>
                  <span v-if="object.type == 'security_measure'" class="badge">
                    {{ $t('projects.view.security_measure_label') }}
                  </span>
                </td>
                <td v-if="tableColumns.scope">
                  <span class="badge" v-if="object.scope == 'policy'">
                    <font-awesome-icon icon="crosshairs"/>
                    {{ $t('projects.scope.policy') }}
                  </span>
                  <span class="badge" v-if="object.scope == 'system'">
                    <font-awesome-icon icon="crosshairs"/>
                    {{ $t('projects.system.label') }}
                  </span>
                  <span class="badge" v-if="object.scope == 'process'">
                    <font-awesome-icon icon="crosshairs"/>
                    {{ $t('projects.process.label') }}
                  </span>
                  <span class="badge" v-if="object.scope == 'activity'">
                    <font-awesome-icon icon="crosshairs"/>
                    {{ $t('projects.activity.label') }}
                  </span>
                  <span class="badge" v-if="object.scope == 'third_party'">
                    <font-awesome-icon icon="crosshairs"/>
                    {{ $t('projects.third_party.label') }}
                  </span>
                </td>
                <td v-if="tableColumns.schedule">
                  <div class="line-height-20" v-if="object.control_type == 'continuous'">
                    <span v-if="object.control_type == 'continuous'">
                      {{ $t('projects.schedule.continuous') }}
                    </span>
                  </div>
                  <div class="text-muted line-height-20" v-if="object.control_type == 'continuous'">
                    <span v-if="object.is_active_child_project">{{ $t('system.currently_running') }}</span>
                    <span v-else-if="object.nextRun">{{ $t('projects.schedule.next_run_table_label') }}:  {{ object.nextRun | formatDate }}</span>
                    <span v-else-if="!object.nextRun">{{ $t('system.not_scheduled') }}</span>
                  </div>
                  <div :class="{'line-height-20': object.start_date}" v-if="object.control_type == 'one time'">
                    <span v-if="object.control_type == 'one time'">
                      {{ $t('projects.schedule.one_time') }}
                    </span>
                  </div>
                  <div class="text-muted line-height-20" v-if="object.start_date && object.control_type == 'one time'">
                    {{ object.start_date | formatDate }} - {{ object.end_date | formatDate }}
                  </div>
                </td>
                <td v-if="tableColumns.status">
                  <span v-if="object.control_type == 'one time'">
                    <span v-if="object.status === 'completed'" class="pr-1">
                      <font-awesome-icon icon="check-circle" class="icon-success"/>
                      {{ $t('system.completed') }}
                    </span>
                    <span v-if="object.status === 'pending'" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-1"/>
                      {{ $t('system.pending') }}
                    </span>
                    <span v-if="object.status === 'in progress'" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
                      {{ $t('system.in_progress') }}
                    </span>
                    <span v-if="object.status === 'closed'" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-12"/>
                      {{ $t('system.closed') }}
                    </span>
                  </span>
                  <span v-if="object.control_type == 'continuous'">
                    <span v-if="!object.is_active_child_project" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-1"/>
                      {{ $t('system.pending') }}
                    </span>
                    <span v-if="object.is_active_child_project" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
                      {{ $t('system.in_progress') }}
                    </span>
                  </span>
                </td>
                <td align="right">
                  <ProjectEditForm
                      :object="object"
                      @reloadContent="loadContent(currentPage)"
                      v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], object) && object.status != 'closed' && object.status != 'completed'"
                  ></ProjectEditForm>
                  <span class="btn btn-delete"
                        qid="delete-s-modal"
                        v-if="isGranted([permissions.PROJECT_DELETE, permissions.PROJECT_DELETE_OWN], object) && !object.hasRunningProjects"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>

        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group v-if="isMember()">
              <label qid="create-s-users-label">
                {{ $t('projects.create.owners_label') }}
              </label>
              <multiselect
                  v-model="filters.owners"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="users"
                  :multiple="true"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :taggable="false"
                  track-by="slug"
                  :placeholder="$t('projects.create.owners_placeholder')"
                  label="name"
                  @search-change="loadUsers"
                  qid="filter-s-form-owners"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
            </b-form-group>
            <hr/>
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('projects.create.type_label') }}
              </label>
              <multiselect
                      v-model="filters.projectType"
                      :options="projectTypes"
                      :placeholder="$t('projects.create.type_placeholder')"
                      @select="debounceInput"
                      @remove="debounceInput"
                      select-label="✔"
                      selected-label="✔"
                      deselect-label="x"
                      track-by="value"
                      label="name"
                      qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('projects.create.scope_label') }}
              </label>
              <multiselect
                  v-model="filters.projectScope"
                  :options="projectScopes"
                  :placeholder="$t('projects.create.scope_placeholder')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="name"
                  label="name"
                  qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('projects.create.schedule_label') }}
              </label>
              <multiselect
                  v-model="filters.projectSchedule"
                  :options="projectSchedules"
                  :placeholder="$t('projects.create.schedule_placeholder')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="name"
                  label="name"
                  qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <b-form-group>
              <label qid="create-do-tags-label">
                {{ $t('processes.create.tags_label') }}
              </label>
              <multiselect
                      v-model="filters.tags"
                      @select="debounceInput"
                      @remove="debounceInput"
                      :options="tags"
                      :multiple="true"
                      :taggable="true"
                      select-label="✔"
                      selected-label="✔"
                      deselect-label="x"
                      track-by="name_translated"
                      :placeholder="$t('processes.create.tags_placeholder')"
                      label="name_translated"
                      qid="filter-p-form-tags"
                      @search-change="loadTags"
                      @tag="addTag"
              ></multiselect>
            </b-form-group>
            <hr/>
            <b-form-group>
              <label qid="create-s-users-label">
                {{ $t('table.last_updated_by') }}
              </label>
              <multiselect
                  v-model="filters.modifiedBy"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="users"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :multiple="false"
                  :taggable="false"
                  track-by="slug"
                  :placeholder="$t('table.last_updated_by')"
                  label="name"
                  @search-change="loadUsers"
                  qid="filter-s-form-modified-by"
              >
                <template slot="singleLabel" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="filter-s-modified-from-label">
                {{ $t('system.modified_from') }}
              </label>
              <datepicker
                  class="date-picker"
                  qid="filter-s-form-modified-from"
                  calendar-class="right"
                  @selected="debounceInput"
                  @remove="debounceInput"
                  v-model="filters.modifiedFrom"
                  :clear-button="true"
                  :placeholder="$t('system.modified_from')"
              ></datepicker>
            </b-form-group>

            <b-form-group>
              <label qid="filter-s-modified-to-label">
                {{ $t('system.modified_to') }}
              </label>
              <datepicker
                  class="date-picker"
                  qid="filter-s-form-modified-to"
                  calendar-class="right"
                  @selected="debounceInput"
                  @remove="debounceInput"
                  v-model="filters.modifiedTo"
                  :clear-button="true"
                  :placeholder="$t('system.modified_to')"
              ></datepicker>
            </b-form-group>

            <a href="#" @click="resetFilters" class="text-right" qid="filter-s-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete confirm modal template -->
    <b-modal ref="delete-activity" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-s-title">
          {{ $t('activities.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('activities.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-s-cancel-button"
        >
          {{ $t('activities.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-s-submit-button"
                  v-if="isGranted([permissions.PROJECT_DELETE, permissions.PROJECT_DELETE_OWN], itemToDelete)"
        >
          {{ $t('activities.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {config} from '@/config.js';
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import Datepicker from 'vuejs-datepicker';
  import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue'
  import ProjectEditForm from '@/views/Projects/ProjectEditForm.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'AssetProjectsList',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('projects.compliance_activity_table.asset_title'),
            active: true
          }
        ],
        tableItems: [],
        itemToDelete: {},
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          projectScope: [],
          projectType: [],
          owners: [],
          modifiedBy: [],
          modifiedFrom: '',
          modifiedTo: '',
          keywordValue: '',
          tags:[],
        },
        tableColumns: {
          responsibles: 1,
          tags: 1,
          type: 1,
          scope: 1,
          schedule: 1,
          status: 1,
        },
        projectTypes: [
          { name: this.$t('projects.type.update'), value: 'update' },
          { name: this.$t('projects.type.validation'), value: 'validation' }
        ],
        projectScopes: [
          { name: this.$t('projects.scope.process'), value: 'process' },
          { name: this.$t('projects.scope.activity'), value: 'activity' },
          { name: this.$t('projects.scope.system'), value: 'system' },
          { name: this.$t('projects.scope.third_party'), value: 'third_party' }
        ],
        projectSchedules: [
          { name: this.$t('projects.schedule.continuous'), value: 'continuous' },
          { name: this.$t('projects.schedule.one_time'), value: 'one time' }
        ],
        projectSchedule: [],
        typeInput: '',
        loading: true,

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        types: ['update','validation'],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,
        users: [],
        selectedUsers: [],
        selectedItem: {},
        showUsersModal: false,
        myGuides: [],
        tags: [],
        newTags: [],
        showTagsModal: false,
      }
    },
    components: {
      ProjectEditForm,
      Avatar,
      Datepicker,
      ShowMoreModal,
      CreateProjectModal,
      GuideButton
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getTableColumns() {
        if (localStorage.getItem('tableColumns')) {
          return JSON.parse(localStorage.getItem('tableColumns'))
        } else {
          return {}
        }
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      },
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent()
        if (this.getTableColumns.asset_project_templates) {
          this.tableColumns = this.getTableColumns.asset_project_templates
        }
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      resetFilters() {
        this.filters.owners = [];
        this.filters.modifiedBy = [];
        this.filters.modifiedFrom = '';
        this.filters.modifiedTo = '';
        this.filters.projectType = [];
        this.filters.projectScope = [];
        this.filters.projectSchedule = [];
        this.filters.tags = [];
        this.debounceInput();
      },
      handleFilterButton(forceOpened) {
        if (!this.filterOpened) {
          this.loadUsers()
          this.loadTags()
        }
        if (forceOpened) {
          this.filterOpened = true
        } else {
          this.filterOpened = !this.filterOpened
        }
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Project' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Project',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      openTagsModal(object) {
        this.selectedItem = object
        this.showTagsModal = true
      },
      closeTagsModal() {
        this.selectedItem = {}
        this.showTagsModal = false
      },
      getReducedTags(tags) {
        let reducedTags = []
        if (tags.length > 3) {
          reducedTags = _.cloneDeep(tags);
          reducedTags = reducedTags.slice(0, 3);
        } else {
          reducedTags = tags;
        }
        return reducedTags;
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params:
                  {
                    client: this.getClient.slug,
                    keyword: query ? query : undefined
                  }
        }).then(function(response) {
          self.tags = response.data
          for (var i = 0; i < self.newTags.length; i++) {
            self.tags.push(self.newTags[i]);
          }
          if (self.tags && !self.tags.length && !query) {
            self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      addTag(newTag) {
        const tag = {
          name: newTag,
          translatable: newTag,
          name_translated: newTag
        }
        this.selectedTags.push(tag)
        this.newTags.push(tag)
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      getReducedUsers(users) {
        let reducedUsers = []
        if (users && users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
        return reducedUsers;
      },
      isMember() {
        if (this.getClient && this.getClient.user_roles && this.getClient.user_roles.length == 1 && this.getClient.user_roles[0].name == 'Member') {
          return false;
        } else {
          return true;
        }
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      handleTypeInput(input) {
        this.typeInput = input;
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        let formOwners = [];
        let formTags = [];

        // Append list of users to FormData
        if (this.filters.owners && this.filters.owners.length) {
          for (var i = 0; i < this.filters.owners.length; i++) {
            formOwners[i] = this.filters.owners[i].id;
          }
          this.handleFilterButton(true)
        }

        if (
          (this.filters.modifiedBy && this.filters.modifiedBy.id) ||
          (this.filters.projectType && this.filters.projectType.value) ||
          (this.filters.projectScope && this.filters.projectScope.value) ||
          (this.filters.projectSchedule && this.filters.projectSchedule.value) ||
          this.filters.modifiedFrom ||
          this.filters.modifiedTo
        ) {
          this.handleFilterButton(true)
        }

        if (this.filters.tags && this.filters.tags.length) {
          for (var t = 0; t < this.filters.tags.length; t++) {
            formTags[t] = this.filters.tags[t].id;
          }
          this.handleFilterButton(true)
        }


        piincHttp.get('projects', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            owners: formOwners ? formOwners : undefined,
            types: ['update','validation'],
            tags: formTags ? formTags : undefined,
            scope: this.filters.projectScope ? this.filters.projectScope.value : undefined,
            modified_by: this.filters.modifiedBy ? this.filters.modifiedBy.id : undefined,
            modified_from: this.filters.modifiedFrom ? this.filters.modifiedFrom : undefined,
            modified_to: this.filters.modifiedTo ? this.filters.modifiedTo : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            control_type: this.filters.projectSchedule ? this.filters.projectSchedule.value : undefined,
            without_parent: 1,
            type: this.filters.projectType ? this.filters.projectType.value : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load Third parties when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load Third parties when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)

      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-activity'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-activity'].hide()
      },
      confirmDeleteS(object) {
        let self = this;
        piincHttp.delete('projects/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('projects.delete.success'))
        }, function() {});
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      }
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'asset_project_templates',
        filters: this.filters
      }
      let columnsObject = {
        type: 'asset_project_templates',
        tableColumns: this.tableColumns
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storeTableColumns', columnsObject);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.asset_project_templates) {
          this.filters = this.getFilters.asset_project_templates
        }
        if (this.getTableColumns.asset_project_templates) {
          this.tableColumns = this.getTableColumns.asset_project_templates
        }
        this.loadContent()
      }
      this.loadGuides('asset_projects');
    }
  }
</script>
